import { useState } from "react";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import type {
    FundDataWithType,
    ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";

import { Translate } from "../../../../../hooks/useTranslation";
import { type SubscribableAsset } from "../../../../../openapi";
import Checkbox from "../../../../Checkbox";
import SvgPencilIcon from "../../../../icons/Pencil";
import FrequencySelector, { type Frequency } from "./FrequencySelector";
import ShareClassSelector from "./ShareClassSelector";
import { type SelectionDocument } from "./types";

type Props = {
    setId: number;
    label: React.ReactNode;
    level: SubscribableAsset["subscriptionLevel"];
    selected?: boolean;
    disabled?: boolean;
    funds: FundDataWithType[];
    shareClasses: ShareClassDataWithType[];
    frequency: Frequency;
    onChange?: (arg: { frequency?: Frequency; selected?: boolean }) => void;
    allDocuments: SelectionDocument[];
};

const SelectDocumentsComponent = ({
    setId,
    label,
    onChange,
    selected,
    disabled,
    shareClasses,
    funds,
    frequency,
    allDocuments,
}: Props) => {
    const { checkboxEvent, buttonClickEvent } = useYardActionEvents();
    const yardEventPosition = "Share class selection";
    const document = allDocuments.find((d) => d.setId === setId);
    const [selectorKey, setSelectorKey] = useState(
        `${frequency}-${Date.now()}`,
    );
    const canShowSCSelect =
        shareClasses.length > 1 &&
        selected &&
        document &&
        Boolean(document?.scSelector);

    const frequencies = [
        {
            key: "DAILY",
            label: <Translate>Same-day email</Translate>,
        },
        {
            key: "WEEKLY",
            label: <Translate>Add to weekly round-up</Translate>,
        },
        {
            key: "MONTHLY",
            label: <Translate>Add to monthly round-up</Translate>,
        },
        {
            key: "QUARTERLY",
            label: <Translate>Add to quarterly round-up</Translate>,
        },
    ] as { label: React.ReactNode; key: Frequency }[];

    const handleChange = ({
        isSelected,
        frequency,
    }: {
        isSelected?: boolean;
        frequency?: Frequency;
    }) => {
        if (!onChange) {
            return;
        }
        if (frequency) {
            onChange({ frequency, selected: true });
        } else {
            onChange({ selected: Boolean(isSelected) });
        }
    };

    return (
        <div
            className={cx(
                "mb-2 grid grid-cols-1 gap-4 sm:grid-cols-[230px_1fr]",
                selected && "mb-4",
            )}
        >
            <div className="max-sm:flex max-sm:justify-between">
                <Checkbox
                    value={setId}
                    label={label}
                    onChange={(_, isSelected) => handleChange({ isSelected })}
                    selected={selected}
                    disabled={disabled}
                    labelClassName="text-sm sm:text-md leading-5 block"
                    containerClassName="items-start"
                    inputContainerClassName="items-start pt-1"
                    inputClassName="w-[13px] h-[13px]"
                />
                {canShowSCSelect && (
                    <div className="flex items-center">
                        <ShareClassSelector
                            key={selectorKey}
                            doc={document}
                            funds={funds}
                            shareClasses={shareClasses}
                            frequency={frequency}
                            onClose={() => {
                                setSelectorKey(`${frequency}-${Date.now()}`);
                                buttonClickEvent({
                                    name: "Cancel share class",
                                    position: yardEventPosition,
                                });
                            }}
                            onOpen={() =>
                                buttonClickEvent({
                                    position: "Document select options",
                                    docType: document.name,
                                    name: "Edit share classes",
                                })
                            }
                            onChange={(shareClasses, selected) =>
                                checkboxEvent({
                                    name: Array.isArray(shareClasses)
                                        ? shareClasses.join(",")
                                        : shareClasses,
                                    position: yardEventPosition,
                                    details: selected ? "True" : "False",
                                })
                            }
                            onSave={() =>
                                buttonClickEvent({
                                    name: "Confirm",
                                    position: yardEventPosition,
                                })
                            }
                        />
                        <SvgPencilIcon
                            width={10}
                            height={10}
                            className="ml-1"
                        />
                    </div>
                )}
            </div>
            {selected && (
                <FrequencySelector
                    value={frequency}
                    onChange={(frequency) => {
                        handleChange({ frequency });
                        setSelectorKey(`${frequency}-${Date.now()}`);
                    }}
                    frequencies={frequencies}
                    className="max-sm:ml-4"
                />
            )}
        </div>
    );
};

export default SelectDocumentsComponent;
